// P0-115 R259: replaces a bare <SplashLoader/> Suspense fallback that has no
// escape hatch. If a lazy chunk fails to resolve (network split, CDN down,
// service worker stuck on stale chunk hash), the user otherwise stares at an
// infinite spinner. After `timeoutMs`, we surface a recovery CTA that reloads
// the window so the browser fetches a fresh chunk manifest.
import { useEffect, useState } from "react";
import { SplashLoader } from "./SplashLoader";

interface SuspenseFallbackTimeoutProps {
  readonly timeoutMs?: number;
}

function reloadWindow(): void {
  if (typeof window !== "undefined") window.location.reload();
}

function logSuspenseStuck(timeoutMs: number): void {
  console.warn(JSON.stringify({ event: "suspense_fallback_stuck", timeoutMs }));
}

function isComprarRoute(): boolean {
  return (
    typeof window !== "undefined" && window.location.pathname === "/comprar"
  );
}

function ComprarLandingSkeleton(): React.JSX.Element {
  return (
    <div
      data-testid="comprar-landing-skeleton"
      data-skeleton="comprar-landing"
      className="fixed inset-0 z-50 bg-background"
      role="status"
      aria-label="Carregando planos"
    />
  );
}

interface StuckRecoveryProps {
  readonly onReload: () => void;
}

function StuckRecovery({ onReload }: StuckRecoveryProps): React.JSX.Element {
  return (
    <div
      data-testid="suspense-fallback-stuck"
      role="alert"
      className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-background px-6 text-center"
    >
      <h2 className="text-base font-semibold text-foreground">
        Carregamento demorado
      </h2>
      <p className="mt-2 text-sm text-foreground/60">
        A página ainda está abrindo. Tente recarregar para forçar uma nova
        tentativa.
      </p>
      <button
        type="button"
        onClick={onReload}
        data-testid="suspense-fallback-reload"
        className="mt-6 rounded-xl border border-border bg-surface px-5 py-2 text-sm font-medium text-foreground/80 hover:bg-surface transition"
      >
        Recarregar
      </button>
    </div>
  );
}

export function SuspenseFallbackTimeout({
  timeoutMs = 10_000,
}: SuspenseFallbackTimeoutProps): React.JSX.Element {
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => {
      logSuspenseStuck(timeoutMs);
      setStuck(true);
    }, timeoutMs);
    return () => clearTimeout(id);
  }, [timeoutMs]);

  if (isComprarRoute()) return <ComprarLandingSkeleton />;
  if (stuck) return <StuckRecovery onReload={reloadWindow} />;
  return <SplashLoader />;
}
